import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * ALPINULIN Geschenk-Wächter — Abo-Geschenke (0-€-Bundles „… (Geschenk)“) gibt es nur mit Abo.
 *
 * Ohne Shopify Plus gibt es keine Checkout-Validierung, die ein Geschenk ohne Abo sperrt.
 * Dieser Wächter räumt deshalb den Warenkorb auf: bei jedem Seitenaufruf und nach jeder
 * Warenkorb-Änderung. Ein Geschenk bleibt nur, wenn eine Abo-Zeile derselben Gruppe
 * (_alp_offer) im Warenkorb liegt, und höchstens in der Menge, mit der die Leiter es hinzufügt (1).
 * Direkte Checkout-Links umgehen das Theme — dafür hält die Abo-App solche Bestellungen an.
 */
const SOURCE = 'alp-gift-guard';

function isGift(line) {
  return Boolean(
    line.properties?._alp_gift ||
      String(line.sku || '').startsWith('GIFT-') ||
      /\(Geschenk\)\s*$/.test(String(line.product_title || ''))
  );
}

function hasMatchingAbo(line, items) {
  const group = line.properties?._alp_offer;
  return items.some(
    (other) => other.selling_plan_allocation && (!group || other.properties?._alp_offer === group)
  );
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

let running = false;

async function guard() {
  if (running) return;
  running = true;
  try {
    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    const updates = {};
    for (const line of cart.items || []) {
      if (!isGift(line)) continue;
      if (!hasMatchingAbo(line, cart.items)) updates[line.key] = 0;
      else if (line.quantity > 1) updates[line.key] = 1;
    }
    if (!Object.keys(updates).length) return;

    const ids = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise?.();
    if (deferred) {
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
          promise: deferred.promise,
        })
      );
    }

    const result = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        ...(ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {}),
      }),
    }).then((r) => r.json());

    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: { items: result.items, sections: result.sections, source: SOURCE, didError: false },
      });
    } else if (window.location.pathname.startsWith('/cart')) {
      window.location.reload();
    }
  } catch (error) {
    console.warn('[alp-gift-guard]', error);
  } finally {
    running = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.detail?.source === SOURCE) return;
  event.promise
    ?.then(({ detail } = {}) => {
      if (detail?.source !== SOURCE) guard();
    })
    .catch(() => {});
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', guard, { once: true });
} else {
  guard();
}
